import type {
    GridApiPremium,
    GridColDef,
    GridColumnOrderChangeParams,
    GridValidRowModel,
    MuiEvent
} from '@mui/x-data-grid-premium';
import type { RefObject } from 'react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import type { FavouriteColumnPresetModel } from '../columnViewTypes';
import {
    applyOrderedFields,
    areOrderedFieldsEqual,
    buildDefaultColumnOrder,
    getOrderedFieldsFromApi,
    isColumnDefFieldOrder,
    mergeOrderedFields,
    orderedFieldsWithoutGroupingColumn
} from '../columnViewUtils';

type UseDataGridColumnOrderParams = {
    /** When false, order apply/restore is disabled and onColumnOrderChange is a passthrough. */
    enabled: boolean;
    apiRef: RefObject<GridApiPremium | null>;
    columns: GridColDef[];
    rows: GridValidRowModel[];
    sizedColumns: GridColDef[];
    columnVisibilityModel: FavouriteColumnPresetModel;
    onColumnOrderChange?: (allColumns: GridColDef[], event: MuiEvent<{}>) => void;
    setColumnOrderDirty: (dirty: boolean) => void;
    bumpColumnOrderRevision: () => void;
};

/**
 * Column-order tracking for saveable column views (`enableSaveColumnView`).
 * When disabled, only forwards `onColumnOrderChange` — no setColumnIndex / restore side effects.
 */
export function useDataGridColumnOrder({
    enabled,
    apiRef,
    columns,
    rows,
    sizedColumns,
    columnVisibilityModel,
    onColumnOrderChange,
    setColumnOrderDirty,
    bumpColumnOrderRevision
}: UseDataGridColumnOrderParams) {
    const pendingOrderedFieldsRef = useRef<string[] | undefined>(undefined);
    const needsOrderRestoreRef = useRef(false);
    const isApplyingOrderRef = useRef(false);
    const suppressOrderChangeRef = useRef(false);
    const sizedColumnFieldsRef = useRef<string[]>([]);
    const baselineOrderedFieldsRef = useRef<string[] | null>(null);
    const restorePendingColumnOrderRef = useRef<() => void>(() => undefined);
    const prevSizedColumnsRef = useRef(sizedColumns);

    const defaultOrderedFields = useMemo(
        () => (columns ?? []).map((column) => column.field).filter(Boolean),
        [columns]
    );

    const captureBaselineOrder = useCallback(() => {
        if (!enabled || baselineOrderedFieldsRef.current?.length) {
            return;
        }
        const fields = getOrderedFieldsFromApi(apiRef);
        if (fields.length) {
            baselineOrderedFieldsRef.current = fields;
        }
    }, [apiRef, enabled]);

    const applyOrderToApi = useCallback(
        (fieldsToApply: string[]) => {
            isApplyingOrderRef.current = true;
            try {
                applyOrderedFields(apiRef, fieldsToApply);
            } finally {
                isApplyingOrderRef.current = false;
            }
        },
        [apiRef]
    );

    const applyOrderedFieldsToGrid = useCallback(
        (orderedFields: string[] | null | undefined) => {
            if (!enabled || orderedFields === undefined) {
                return;
            }

            const currentFields = getOrderedFieldsFromApi(apiRef);

            if (orderedFields === null) {
                const fieldsToApply = baselineOrderedFieldsRef.current?.length
                    ? mergeOrderedFields(baselineOrderedFieldsRef.current, currentFields)
                    : buildDefaultColumnOrder(defaultOrderedFields, currentFields);
                if (!fieldsToApply.length) {
                    return;
                }
                pendingOrderedFieldsRef.current = fieldsToApply;
                needsOrderRestoreRef.current = true;
                applyOrderToApi(fieldsToApply);
                setColumnOrderDirty(false);
                bumpColumnOrderRevision();
                return;
            }

            captureBaselineOrder();
            const fieldsToApply = mergeOrderedFields(orderedFields, currentFields);
            if (!fieldsToApply.length) {
                return;
            }

            pendingOrderedFieldsRef.current = fieldsToApply;
            needsOrderRestoreRef.current = true;
            applyOrderToApi(fieldsToApply);
            setColumnOrderDirty(false);
            bumpColumnOrderRevision();
        },
        [
            apiRef,
            applyOrderToApi,
            bumpColumnOrderRevision,
            captureBaselineOrder,
            defaultOrderedFields,
            enabled,
            setColumnOrderDirty
        ]
    );

    const getCurrentOrderedFields = useCallback(() => getOrderedFieldsFromApi(apiRef), [apiRef]);

    useLayoutEffect(() => {
        if (!enabled) {
            return;
        }
        captureBaselineOrder();
    }, [captureBaselineOrder, columns, enabled, rows]);

    const handleColumnOrderChange = useCallback(
        (params: GridColumnOrderChangeParams, event: MuiEvent<{}>) => {
            if (!enabled) {
                if (typeof onColumnOrderChange === 'function') {
                    onColumnOrderChange(apiRef.current?.getAllColumns() ?? [], event);
                }
                return;
            }

            if (isApplyingOrderRef.current || suppressOrderChangeRef.current) {
                return;
            }

            const currentOrder = getOrderedFieldsFromApi(apiRef);
            const oldIndex = params?.oldIndex;
            const targetIndex = params?.targetIndex;
            if (typeof oldIndex !== 'number' || typeof targetIndex !== 'number' || oldIndex === targetIndex) {
                return;
            }

            // MUI usually emits this after the column has already moved, so the field is no longer at oldIndex.
            let nextOrder = currentOrder;
            if (currentOrder[oldIndex] === params.column?.field) {
                nextOrder = [...currentOrder];
                const [moved] = nextOrder.splice(oldIndex, 1);
                nextOrder.splice(targetIndex, 0, moved);
            }

            const pending = pendingOrderedFieldsRef.current;
            const defFields = sizedColumnFieldsRef.current;
            if (
                pending?.length &&
                defFields.length &&
                !areOrderedFieldsEqual(pending, nextOrder) &&
                isColumnDefFieldOrder(nextOrder, defFields) &&
                !isColumnDefFieldOrder(pending, defFields)
            ) {
                needsOrderRestoreRef.current = true;
                queueMicrotask(() => restorePendingColumnOrderRef.current());
                return;
            }

            const referenceOrder = pending?.length ? pending : (baselineOrderedFieldsRef.current ?? []);
            if (
                referenceOrder.length > 0 &&
                areOrderedFieldsEqual(
                    orderedFieldsWithoutGroupingColumn(nextOrder),
                    orderedFieldsWithoutGroupingColumn(referenceOrder)
                )
            ) {
                return;
            }

            pendingOrderedFieldsRef.current = nextOrder;
            needsOrderRestoreRef.current = true;
            setColumnOrderDirty(true);
            bumpColumnOrderRevision();
            if (typeof onColumnOrderChange === 'function') {
                onColumnOrderChange(apiRef.current?.getAllColumns() ?? [], event);
            }
        },
        [apiRef, bumpColumnOrderRevision, enabled, onColumnOrderChange, setColumnOrderDirty]
    );

    if (enabled) {
        if (prevSizedColumnsRef.current !== sizedColumns) {
            suppressOrderChangeRef.current = true;
            prevSizedColumnsRef.current = sizedColumns;
        }
        sizedColumnFieldsRef.current = sizedColumns.map((column) => column.field);
    }

    const restorePendingColumnOrder = useCallback(() => {
        if (!enabled || !needsOrderRestoreRef.current) {
            return;
        }
        const orderedFields = pendingOrderedFieldsRef.current;
        if (!orderedFields?.length) {
            return;
        }
        const currentFields = getOrderedFieldsFromApi(apiRef);
        if (!currentFields.length) {
            return;
        }
        if (areOrderedFieldsEqual(currentFields, mergeOrderedFields(orderedFields, currentFields))) {
            return;
        }
        applyOrderToApi(orderedFields);
    }, [apiRef, applyOrderToApi, enabled]);
    restorePendingColumnOrderRef.current = restorePendingColumnOrder;

    // After MUI's own effects (visibility / column rebuild) may have reset order.
    useEffect(() => {
        if (!enabled) {
            return;
        }
        restorePendingColumnOrder();
        suppressOrderChangeRef.current = false;
    }, [columnVisibilityModel, enabled, restorePendingColumnOrder, rows, sizedColumns]);

    return {
        defaultOrderedFields,
        getCurrentOrderedFields,
        applyOrderedFieldsToGrid,
        handleColumnOrderChange
    };
}
